import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import "./AnalyticsDashboard.css";

const COLORS = [
  "#48bffa",
  "#3a9bd9",
  "#2c7bb8",
  "#1a5a97",
  "#8884D8",
  "#82CA9D",
];

const AnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  // Particle effect for the background (same as Home page)
  useEffect(() => {
    const createParticle = () => {
      const particle = document.createElement("div");
      particle.className = "particle";

      const size = Math.random() * 15 + 5;
      particle.style.width = `${size}px`;
      particle.style.height = `${size}px`;

      particle.style.left = `${Math.random() * 100}vw`;
      particle.style.top = `${Math.random() * 100}vh`;

      particle.style.animationDuration = `${Math.random() * 6 + 6}s`;

      const container = document.querySelector(".hero-gradient");
      if (container) {
        container.appendChild(particle);
      }

      setTimeout(() => {
        particle.remove();
      }, 12000);
    };

    const interval = setInterval(() => {
      if (document.querySelector(".hero-gradient")) {
        createParticle();
      }
    }, 300);

    return () => clearInterval(interval);
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/analytics/stats`
      );
      const analyticsData = await response.json();
      setData(analyticsData);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 z-10">
          <div className="w-full h-full grid-pattern" />
        </div>
        <div className="absolute inset-0 opacity-90 bg-gradient-to-br from-brand-deep/60 via-brand-primary/50 to-brand-accent/40 z-20"></div>
        <div className="relative z-30 text-xl">Loading analytics...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 z-10">
          <div className="w-full h-full grid-pattern" />
        </div>
        <div className="absolute inset-0 opacity-90 bg-gradient-to-br from-brand-deep/60 via-brand-primary/50 to-brand-accent/40 z-20"></div>
        <div className="relative z-30 text-xl text-red-400">
          Failed to load analytics data
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <div
      className="min-h-screen hero-gradient relative overflow-hidden"
      style={{ marginTop: 50 }}
    >
      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 opacity-30 z-10">
        <div className="w-full h-full grid-pattern" />
      </div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 opacity-90 bg-gradient-to-br from-brand-deep/60 via-brand-primary/50 to-brand-accent/40 z-20"></div>

      <div className="relative z-30 p-6">
        <div id="analytics_top" className="max-w-7xl mx-auto">
          <div className="mb-8 text-center">
            <div className="service-icon mx-auto mb-4">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-brand-light to-white bg-clip-text text-transparent">
              Analytics Dashboard
            </h1>
            <p className="text-brand-light/80 text-lg">
              Website traffic and visitor insights
            </p>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="service-card">
              <div className="flex items-center mb-4">
                <div className="service-icon w-12 h-12 mr-4">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-brand-light/80">
                    Total Page Views
                  </h3>
                  <p className="text-3xl font-bold text-brand-light">
                    {(data.summary.totalPageViews || 0).toLocaleString()}
                  </p>
                </div>
              </div>
              <p className="text-sm text-brand-light/60">Last 30 days</p>
            </div>
            <div className="service-card">
              <div className="flex items-center mb-4">
                <div className="service-icon w-12 h-12 mr-4">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-brand-light/80">
                    Unique Visitors
                  </h3>
                  <p className="text-3xl font-bold text-brand-light">
                    {(data.summary.totalUniqueVisitors || 0).toLocaleString()}
                  </p>
                </div>
              </div>
              <p className="text-sm text-brand-light/60">Last 30 days</p>
            </div>
            <div className="service-card">
              <div className="flex items-center mb-4">
                <div className="service-icon w-12 h-12 mr-4">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-brand-light/80">
                    Avg Daily Views
                  </h3>
                  <p className="text-3xl font-bold text-brand-light">
                    {(data.summary.avgDailyViews || 0).toLocaleString()}
                  </p>
                </div>
              </div>
              <p className="text-sm text-brand-light/60">Last 30 days</p>
            </div>
          </div>

          {/* Daily Traffic Chart */}
          <div className="service-card mb-8">
            <h2 className="text-xl font-semibold mb-4 text-brand-light">
              Daily Traffic
            </h2>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={data.dailyStats || []}
                  margin={{ left: 0, right: 20, top: 10, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(72, 191, 250, 0.1)"
                  />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDate}
                    tick={{ fill: "white", fontSize: 10 }}
                  />
                  <YAxis tick={{ fill: "white", fontSize: 10 }} width={25} />
                  <Tooltip
                    labelFormatter={(value) => formatDate(value)}
                    contentStyle={{
                      backgroundColor: "rgba(26, 47, 72, 0.95)",
                      border: "1px solid #48bffa",
                      borderRadius: "8px",
                      color: "white",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="page_views"
                    stroke="#48bffa"
                    name="Page Views"
                    strokeWidth={3}
                  />
                  <Line
                    type="monotone"
                    dataKey="unique_visitors"
                    stroke="white"
                    name="Unique Visitors"
                    strokeWidth={3}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Top Countries */}
            <div className="service-card">
              <h2 className="text-xl font-semibold mb-4 text-brand-light">
                Top Countries
              </h2>
              <div className="space-y-3">
                {(data.countryStats || []).slice(0, 10).map((country) => (
                  <div
                    key={country.country}
                    className="flex justify-between items-center"
                  >
                    <span className="text-sm font-medium text-brand-light">
                      {country.country}
                    </span>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-brand-light/70">
                        {country.visits} visits
                      </span>
                      <div
                        className="bg-brand-accent h-2 rounded"
                        style={{
                          width: `${
                            (country.visits /
                              (data.countryStats?.[0]?.visits || 1)) *
                              100 || 0
                          }px`,
                          minWidth: "4px",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Device Types */}
            <div className="service-card">
              <h2 className="text-xl font-semibold mb-4 text-brand-light">
                Device Types
              </h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.deviceStats || []}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(props) =>
                        `${props.device_type} ${
                          props.percent ? (props.percent * 100).toFixed(0) : 0
                        }%`
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="visits"
                    >
                      {(data.deviceStats || []).map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "rgba(26, 47, 72, 0.95)",
                        border: "1px solid #48bffa",
                        borderRadius: "8px",
                        color: "white",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Bottom Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Top Pages */}
            <div className="service-card">
              <h2 className="text-xl font-semibold mb-4 text-brand-light">
                Top Pages
              </h2>
              <div className="space-y-3">
                {(data.topPages || []).slice(0, 8).map((page) => (
                  <div
                    key={page.path}
                    className="flex justify-between items-center"
                  >
                    <span className="text-sm font-medium truncate max-w-xs text-brand-light">
                      {page.path}
                    </span>
                    <span className="text-sm text-brand-light/70">
                      {page.views} views
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Traffic Sources */}
            <div className="service-card">
              <h2 className="text-xl font-semibold mb-4 text-brand-light">
                Traffic Sources
              </h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={data.referrerStats || []}
                    layout="horizontal"
                    margin={{ left: -10, right: 20, top: 10, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(72, 191, 250, 0.1)"
                    />
                    <XAxis
                      type="number"
                      tick={{ fill: "white", fontSize: 10 }}
                    />
                    <YAxis
                      dataKey="source"
                      type="category"
                      width={80}
                      tick={{ fill: "white", fontSize: 10 }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "rgba(26, 47, 72, 0.95)",
                        border: "1px solid #48bffa",
                        borderRadius: "8px",
                        color: "white",
                      }}
                    />
                    <Bar dataKey="visits" fill="#48bffa" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
